import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, apiPost } from '@/api/client'

interface User {
  identifier: string
  email?: string
  displayName?: string
  roles: string[]
}

/**
 * Store d'auth — session cookie côté Symfony, on garde juste l'identité
 * en mémoire côté Vue pour l'affichage. `fetchMe()` au boot pour décider
 * login vs app.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const checked = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin  = computed(() => (user.value?.roles ?? []).includes('ROLE_ADMIN'))
  /* canEdit = peut muter les données métier (events, tasks, polls, etc.).
   * Hiérarchie côté backend : ADMIN → EDITOR → VIEWER → USER. */
  const canEdit = computed(() =>
    (user.value?.roles ?? []).some(r => r === 'ROLE_ADMIN' || r === 'ROLE_EDITOR')
  )
  /* Rôle restreint « Gantt seul » : a ROLE_GANTT_VIEWER sans aucun rôle
   * supérieur → accès limité à la seule page Gantt. */
  const isGanttOnly = computed(() => {
    const r = user.value?.roles ?? []
    return r.includes('ROLE_GANTT_VIEWER')
      && !r.some(x => x === 'ROLE_ADMIN' || x === 'ROLE_EDITOR' || x === 'ROLE_VIEWER')
  })
  /* Rôle restreint « Editeurs seul » : a ROLE_EDITEURS_VIEWER sans aucun
   * rôle supérieur → accès limité à la seule page Editeurs. */
  const isEditeursOnly = computed(() => {
    const r = user.value?.roles ?? []
    return r.includes('ROLE_EDITEURS_VIEWER')
      && !r.some(x => x === 'ROLE_ADMIN' || x === 'ROLE_EDITOR' || x === 'ROLE_VIEWER')
  })

  async function fetchMe(): Promise<boolean> {
    try {
      user.value = await api<User>('/auth/me', { silentAuth: true })
      return true
    } catch {
      user.value = null
      return false
    } finally {
      checked.value = true
    }
  }

  async function login(username: string, password: string): Promise<void> {
    await apiPost('/auth/login', { username, password })
    await fetchMe()
  }

  async function logout(): Promise<void> {
    try {
      await api('/auth/logout', { method: 'GET' })
    } catch {
      // Le firewall logout renvoie une redirection que fetch refuse en
      // mode same-origin avec credentials → on l'ignore, la session
      // a quand même été détruite côté serveur.
    }
    user.value = null
    checked.value = true
  }

  return { user, checked, isAuthenticated, isAdmin, canEdit, isGanttOnly, isEditeursOnly, fetchMe, login, logout }
})
